import clsx from "clsx";
import type { ElementType, ReactElement, ReactNode } from "react";
import { NavLink } from "react-router-dom";

import type {
	ButtonBorderRadiusVariants,
	ButtonSizeVariants,
	ButtonThemeVariants,
	ButtonVariantsProps,
} from "~/components/buttons/Button/buttonVariants.ts";
import { buttonVariants } from "~/components/buttons/Button/buttonVariants.ts";

interface ButtonProps {
	children?: ReactNode;
	disabled?: boolean;
	fullWidth?: ButtonVariantsProps["fullWidth"];
	isGrouped?: boolean;
	onClick?: (data?: any) => void;
	rounded?: ButtonBorderRadiusVariants;
	size?: ButtonSizeVariants;
	theme?: ButtonThemeVariants;
	to?: string;
	as?: ElementType;
}

type ButtonOrAnchorProps =
	ButtonProps
	& (React.ButtonHTMLAttributes<HTMLButtonElement> | React.AnchorHTMLAttributes<HTMLAnchorElement>);

const Button: React.FC<ButtonOrAnchorProps> = ({
	children,
	isGrouped,
	fullWidth,
	onClick,
	theme = "secondary",
	size,
	className,
	disabled,
	rounded,
	to,
	as = "button",
	...rest
}): ReactElement => {
	const buttonClasses = clsx(
		buttonVariants({ theme, size, disabled, fullWidth, isGrouped, rounded }),
		className,
	);

	const Component: ElementType = to ? NavLink : as;

	const handleClick = onClick
		? (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (as !== "a" && "submit" !== (rest as React.ButtonHTMLAttributes<HTMLButtonElement>).type) {
				e.preventDefault();
			}
			onClick(e);
		}
		: undefined;

	if (to) {
		return (
			<Component to={to}
					   className={buttonClasses} {...rest}>
				{children}
			</Component>
		);
	}

	return (
		<Component className={buttonClasses}
				   onClick={handleClick} {...rest}>
			{children}
		</Component>
	);
};

export default Button;
